import type { GQL } from '@aline/graphql';
import type { Range } from 'slate';
import { Editor, Text, Transforms } from 'slate';

import { CANNED_FIELD_METADATA } from '../../canned-fields/canned-field-metadata';
import type { UUID } from '../../types/type-helpers';
import type { AlineTextFormats, CannedFieldElement, EditingMode } from '../internal';
import { AlineTransforms, cannedFieldType, isCannedFieldElement, Queries } from '../internal';

interface InsertCannedFieldOptions {
  /** Stable CannedField nodeId shared by every placement of the DB row. */
  cannedFieldId: UUID;
  /** Names the occurrence this call inserts. See `CannedFieldElement`. */
  placementNodeId: UUID;
  /** Which canned variant to insert (DateSigned, UserName, …). */
  variant: GQL.CannedFieldVariant;
  /** Insert location. Defaults to editor.selection. */
  at?: Range;
  select?: boolean;
}

export function createCannedFieldElement(args: {
  cannedFieldId: UUID;
  placementNodeId: UUID;
  textFormats: AlineTextFormats;
  variant: GQL.CannedFieldVariant;
}): CannedFieldElement {
  const { cannedFieldId, placementNodeId, textFormats, variant } = args;
  return {
    cannedFieldId,
    children: [
      {
        ...textFormats,
        comments: [],
        suggestedEdits: [],
        text: CANNED_FIELD_METADATA[variant].label,
      },
    ],
    placementNodeId,
    suggestedEdits: [],
    type: cannedFieldType,
    variant,
  };
}

const CannedFieldTransforms = {
  insertCannedField: (
    editor: Editor,
    options: InsertCannedFieldOptions & {
      editingMode: EditingMode;
      textFormats?: AlineTextFormats;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const {
        at = editor.selection,
        cannedFieldId,
        editingMode,
        placementNodeId,
        select = true,
        textFormats,
        variant,
      } = options;
      if (!at) return;

      const resolvedRange = AlineTransforms.nudgeRangeToEdges(editor, { at });
      const resolvedTextFormats: AlineTextFormats =
        textFormats ?? Queries.activeStyleSheet(editor)['style-normal'].styles;
      const locationAfterUnwrap = AlineTransforms.unwrapInlines(editor, {
        at: resolvedRange,
        editingMode,
        voids: true,
      });
      if (!locationAfterUnwrap) return;

      const cannedField = createCannedFieldElement({
        cannedFieldId,
        placementNodeId,
        textFormats: resolvedTextFormats,
        variant,
      });

      Transforms.insertNodes(editor, cannedField, {
        at: locationAfterUnwrap,
        select,
        voids: true,
      });
    });
  },

  /**
   * Removes the single occurrence named by `placementNodeId`, leaving every
   * other placement of the same field in place. The Slate counterpart of
   * deleting one PDF canned widget by its own nodeId.
   */
  removeCannedFieldPlacement: (editor: Editor, args: { placementNodeId: UUID }): void => {
    const { placementNodeId } = args;
    Transforms.removeNodes(editor, {
      at: [],
      match: node => isCannedFieldElement(node) && node.placementNodeId === placementNodeId,
      voids: true,
    });
  },

  removeCannedFieldsById: (editor: Editor, args: { cannedFieldId: UUID }): void => {
    const { cannedFieldId } = args;
    AlineTransforms.withoutNormalizing(editor, () => {
      const entries = Queries.cannedFieldEntries(editor).filter(
        ([el]) => el.cannedFieldId === cannedFieldId
      );
      for (const [, path] of entries.reverse()) {
        Transforms.removeNodes(editor, { at: path, voids: true });
      }
    });
  },

  setCannedFieldValueText: (editor: Editor, args: { cannedFieldId: UUID; text: string }): void => {
    const { cannedFieldId, text } = args;
    setCannedFieldValuesText(editor, { values: new Map([[cannedFieldId, text]]) });
  },

  setCannedFieldValuesText,
};

function setCannedFieldValuesText(
  editor: Editor,
  args: { values: ReadonlyMap<UUID, string> }
): void {
  const { values } = args;
  AlineTransforms.withoutNormalizing(editor, () => {
    for (const [cannedElement, cannedFieldPath] of Queries.cannedFieldEntries(editor)) {
      const text = values.get(cannedElement.cannedFieldId);
      if (typeof text === 'string') {
        const textPath = [...cannedFieldPath, 0];
        const [textNode] = Editor.node(editor, textPath);
        if (Text.isText(textNode) && textNode.text !== text) {
          Transforms.delete(editor, {
            at: {
              anchor: { offset: 0, path: textPath },
              focus: { offset: textNode.text.length, path: textPath },
            },
            voids: true,
          });
          Transforms.insertText(editor, text, {
            at: { offset: 0, path: textPath },
            voids: true,
          });
        }
      }
    }
  });
}

export default CannedFieldTransforms;
